/**
 * Alpine.js component factories for review dashboard charts.
 * Each factory receives pre-formatted data from Go templates (with translated labels)
 * and creates an ApexCharts instance.
 *
 * Theme-aware: reads DaisyUI CSS custom properties at runtime so charts
 * adapt to both dark and light modes.
 *
 * Generic chart machinery (theme color resolution, render queue, ApexCharts
 * loader, base options) lives in `chart-core.ts` and is imported below.
 */

import {
  hexAlpha,
  themeColors,
  waitForApexCharts,
  waitForVisible,
  enqueueRender,
  baseOpts,
  themeAware,
} from "./chart-core";

// ---------------------------------------------------------------------------
// Review-chart-specific palette helpers
// ---------------------------------------------------------------------------

/** Two-color palette: own (copper) + competitor (visible muted). */
function chartColors(): [string, string] {
  const t = themeColors();
  // Competitor color: text color at 30% opacity — visible in both themes
  return [t.secondary, hexAlpha(t.textColor, 0.3)];
}

/** Multi-series palette for stacked/grouped charts. */
function extendedPalette(): string[] {
  const t = themeColors();
  return [
    t.secondary, // copper - own primary
    "#7c9eb2", // steel blue
    t.success, // emerald
    "#c9a86c", // warm gold
    "#a78baf", // muted lavender
    "#e8927c", // salmon
    "#6eb5a0", // sage
    "#b8956a", // caramel
  ];
}

function baseLegend() {
  const t = themeColors();
  return {
    legend: {
      labels: { colors: t.textColor },
      position: "bottom" as const,
      fontSize: "12px",
      markers: { size: 6, shape: "circle" as const },
      itemMargin: { horizontal: 12, vertical: 4 },
    },
  };
}

/** Standard axis label style. */
function axisLabelStyle() {
  const t = themeColors();
  return { colors: t.mutedText, fontSize: "11px", fontWeight: 400 };
}

/** Format a number cleanly: no trailing zeros. */
function fmtNum(v: number): string {
  if (v == null || isNaN(v)) return "";
  return Number(v.toFixed(1)).toString();
}

// ---------------------------------------------------------------------------
// 1. Category Rating Chart — grouped bar (own vs competitor)
// ---------------------------------------------------------------------------
export function categoryRatingChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const [ownColor, compColor] = chartColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 350 },
          series: data.series,
          plotOptions: { bar: { borderRadius: 5, columnWidth: "50%" } },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            min: 0,
            max: 10,
            tickAmount: 5,
            labels: { style: axisLabelStyle(), formatter: fmtNum },
          },
          colors: [ownColor, compColor],
          tooltip: {
            ...opts.tooltip,
            y: { formatter: (v: number) => fmtNum(v) + " / 10" },
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 2. Score & Volume Chart — mixed line + bar, dual Y-axis
// ---------------------------------------------------------------------------
export function scoreVolumeChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const [ownColor, compColor] = chartColors();
        const opts = baseOpts();
        // Lighter versions for volume bars
        const ownVolumeColor = hexAlpha(t.secondary, 0.3);
        const compVolumeColor = hexAlpha(t.textColor, 0.15);
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "line" as const, height: data.height || 380 },
          series: data.series,
          stroke: {
            width: data.series.map((s: any) => (s.type === "bar" ? 0 : 3)),
            curve: "smooth" as const,
          },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: [
            {
              seriesName: data.scoreNames || [],
              min: 0,
              max: 10,
              tickAmount: 5,
              title: {
                text: data.scoreAxisLabel || "Score",
                style: { color: t.mutedText, fontSize: "11px" },
              },
              labels: { style: axisLabelStyle(), formatter: fmtNum },
            },
            {
              seriesName: data.volumeNames || [],
              opposite: true,
              title: {
                text: data.volumeAxisLabel || "Reviews",
                style: { color: t.mutedText, fontSize: "11px" },
              },
              labels: {
                style: axisLabelStyle(),
                formatter: (v: number) => Math.round(v).toString(),
              },
            },
          ],
          colors: [ownColor, compColor, ownVolumeColor, compVolumeColor],
          markers: { size: [4, 4, 0, 0] },
          tooltip: { ...opts.tooltip, y: { formatter: fmtNum } },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 3. Platform Mini Chart — compact grouped bar for platform cards
// ---------------------------------------------------------------------------
export function platformMiniChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const [ownColor, compColor] = chartColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          chart: {
            ...opts.chart,
            type: "bar" as const,
            height: data.height || 180,
            sparkline: { enabled: false },
          },
          series: data.series,
          xaxis: {
            categories: data.categories,
            labels: { style: { ...axisLabelStyle(), fontSize: "9px" } },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: { min: 0, max: 10, show: false },
          plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
          legend: { show: false },
          grid: { ...opts.grid, padding: { left: 4, right: 4, top: -10, bottom: -5 } },
          colors: [ownColor, compColor],
          tooltip: { ...opts.tooltip, y: { formatter: (v: number) => fmtNum(v) + " / 10" } },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 4. Theme Comparison Chart — diverging horizontal bar
// ---------------------------------------------------------------------------
export function themeComparisonChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const t = themeColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: {
            ...opts.chart,
            type: "bar" as const,
            height: data.height || 400,
            stacked: true,
          },
          series: data.series,
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: "65%",
              borderRadius: 3,
            },
          },
          xaxis: {
            categories: data.categories,
            labels: {
              formatter: (v: number) => Math.abs(v).toString(),
              style: axisLabelStyle(),
            },
          },
          yaxis: {
            labels: { style: axisLabelStyle() },
          },
          colors: data.colors || [
            t.secondary,
            hexAlpha(t.textColor, 0.3),
            t.error,
            hexAlpha(t.error, 0.4),
          ],
          tooltip: {
            ...opts.tooltip,
            y: { formatter: (v: number) => Math.abs(v).toString() },
          },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 5. Guest Type Distribution — two donut charts side-by-side (own vs competitor)
// ---------------------------------------------------------------------------
export function guestTypeDistChart(data: any) {
  return themeAware({
    ownChart: null as any,
    compChart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.ownChart);
        if (!self.$refs.ownChart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const palette = extendedPalette();
        const t = themeColors();
        const opts = baseOpts();

        const donutOpts = (series: number[], el: HTMLElement) => ({
          ...opts,
          chart: { ...opts.chart, type: "donut" as const, height: 280 },
          series,
          labels: data.labels,
          colors: palette.slice(0, data.labels.length),
          plotOptions: {
            pie: {
              donut: {
                size: "60%",
                labels: {
                  show: true,
                  name: { show: true, color: t.textColor, fontSize: "13px" },
                  value: { show: true, color: t.mutedText, fontSize: "16px", fontWeight: 600 },
                  total: {
                    show: true,
                    color: t.mutedText,
                    fontSize: "12px",
                    label: "Total",
                    formatter: (w: any) =>
                      w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString(),
                  },
                },
              },
            },
          },
          legend: {
            position: "bottom" as const,
            labels: { colors: t.textColor },
            fontSize: "11px",
            markers: { size: 6, shape: "circle" as const },
          },
          stroke: { show: false },
          dataLabels: { enabled: false },
          tooltip: { ...opts.tooltip, y: { formatter: (v: number) => v.toString() } },
        });

        self.ownChart = new AC(self.$refs.ownChart, donutOpts(data.ownSeries, self.$refs.ownChart));
        self.ownChart.render();

        if (data.hasComp) {
          self.compChart = new AC(
            self.$refs.compChart,
            donutOpts(data.compSeries, self.$refs.compChart),
          );
          self.compChart.render();
        }
      });
    },
    destroy() {
      this.ownChart?.destroy();
      this.compChart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 6. Guest Type by Month Chart — stacked bar + competitor overlay line
// ---------------------------------------------------------------------------
export function guestTypeByMonthChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const palette = extendedPalette();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 380, stacked: true },
          series: data.series,
          plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
          stroke: {
            width: data.series.map((s: any) => (s.type === "line" ? 3 : 0)),
            curve: "smooth" as const,
          },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: {
              style: axisLabelStyle(),
              // Opt-in decimals, defaulting to 0. THIS chart has two consumers:
              // property_hub_reviews plots integer guest COUNTS (whole numbers are
              // correct there) and admin_spend_costs plots DOLLARS, where Math.round
              // collapsed a low-spend fleet to 0/0/0/1. A blanket valueDecimals:2
              // would render guest counts as "3.00", so the spend consumer opts in
              // and the reviews consumer stays silent. The three sibling charts in
              // this file keep Math.round: none of them is reused for currency.
              formatter: (v: number) => v.toFixed(data.valueDecimals ?? 0),
            },
          },
          colors: palette.slice(0, data.series.length),
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 7. Stay Duration Chart — grouped bar (own vs competitor)
// ---------------------------------------------------------------------------
export function stayDurationChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const [ownColor, compColor] = chartColors();
        const t = themeColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 320 },
          series: data.series,
          plotOptions: { bar: { borderRadius: 5, columnWidth: "50%" } },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            title: { text: data.yAxisLabel || "", style: { color: t.mutedText, fontSize: "11px" } },
            labels: { style: axisLabelStyle(), formatter: fmtNum },
          },
          colors: [ownColor, compColor],
          tooltip: { ...opts.tooltip, y: { formatter: (v: number) => fmtNum(v) + " nights" } },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 8. Origin Countries Chart — horizontal bar (top 10, own vs comp)
// ---------------------------------------------------------------------------
export function originCountriesChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const [ownColor, compColor] = chartColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 400 },
          series: data.series,
          plotOptions: {
            bar: { horizontal: true, barHeight: "60%", borderRadius: 3 },
          },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle(), formatter: (v: number) => Math.round(v).toString() },
          },
          yaxis: {
            labels: { style: axisLabelStyle() },
          },
          colors: [ownColor, compColor],
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 9. Seasonal Duration Chart — line chart (own vs comp avg nights per month)
// ---------------------------------------------------------------------------
export function seasonalDurationChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const [ownColor, compColor] = chartColors();
        const t = themeColors();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "line" as const, height: data.height || 320 },
          series: data.series,
          stroke: { width: 3, curve: "smooth" as const },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            title: { text: data.yAxisLabel || "", style: { color: t.mutedText, fontSize: "11px" } },
            labels: { style: axisLabelStyle(), formatter: fmtNum },
          },
          colors: [ownColor, compColor],
          markers: { size: 5, strokeWidth: 2, hover: { size: 7 } },
          tooltip: { ...opts.tooltip, y: { formatter: (v: number) => fmtNum(v) + " nights" } },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}

// ---------------------------------------------------------------------------
// 10. Seasonal Countries Chart — stacked bar (country counts per month)
// ---------------------------------------------------------------------------
export function seasonalCountriesChart(data: any) {
  return themeAware({
    chart: null as any,
    async init() {
      const self = this as any;
      await enqueueRender(async () => {
        await waitForApexCharts();
        await waitForVisible(self.$refs.chart);
        if (!self.$refs.chart?.isConnected) return;
        const AC = (window as any).ApexCharts;
        const palette = extendedPalette();
        const opts = baseOpts();
        self.chart = new AC(self.$refs.chart, {
          ...opts,
          ...baseLegend(),
          chart: { ...opts.chart, type: "bar" as const, height: data.height || 350, stacked: true },
          series: data.series,
          plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
          xaxis: {
            categories: data.categories,
            labels: { style: axisLabelStyle() },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
          yaxis: {
            labels: { style: axisLabelStyle(), formatter: (v: number) => Math.round(v).toString() },
          },
          colors: palette.slice(0, data.series.length),
          tooltip: { ...opts.tooltip },
        });
        self.chart.render();
      });
    },
    destroy() {
      this.chart?.destroy();
    },
  });
}
